import { PropsWithChildren, ReactNode } from 'react';
import cn from 'classnames';
import { PropsWithClassNameAndChildren } from '@orionprotocol/utils';

import { WithTooltip } from '../WithTooltip';
import { Icon } from '../Icon';
import { Loadable } from '../Loadable';

import styles from './Section.module.scss';

interface SectionProps {
  // [demidn] TODO: Only radius is added for md
  size?: 'sm' | 'md';
  type?: 'primary' | 'secondary';
  loading?: boolean;
}

function Root({
  className,
  children,
  size = 'md',
  type = 'primary',
  loading = false,
}: PropsWithClassNameAndChildren<SectionProps>): JSX.Element {
  return (
    <div className={cn(className, styles.root, styles[size], styles[type])}>
      <Loadable loading={loading}>{children}</Loadable>
    </div>
  );
}

function Head({ children }: PropsWithChildren<unknown>): JSX.Element {
  return <div className={cn(styles.head, 'sectionHead')}>{children}</div>;
}

interface BasicProps extends SectionProps {
  title?: ReactNode;
  titleStyle?: 'default' | 'large';
  tip?: string;
  // TODO: [demidn] might be to think a bit more about implementation. possible ways - pass class, pass color
  tipStyle?: 'warning' | 'default';
}

function Basic({
  title,
  titleStyle = 'default',
  children,
  className,
  tip,
  tipStyle = 'default',
  ...props
}: PropsWithClassNameAndChildren<BasicProps>): JSX.Element {
  return (
    <Root
      {...props}
      className={cn(className, styles.basic, {
        [styles.withTitle]: title !== undefined,
      })}
    >
      {title !== undefined && (
        <div className={cn(styles.title, styles[titleStyle])}>
          {title}
          {tip !== undefined && (
            <WithTooltip
              tooltip={<div className={styles.tooltipText}>{tip}</div>}
              width={250}
              side="bottom"
            >
              <div>
                <Icon
                  icon="infoSolid"
                  className={cn(styles.infoIcon, styles[tipStyle])}
                />
              </div>
            </WithTooltip>
          )}
        </div>
      )}
      <div className={styles.content}>{children}</div>
    </Root>
  );
}

function WithHead({
  title,
  children,
  className,
  ...props
}: PropsWithClassNameAndChildren<
  SectionProps & { title?: ReactNode }
>): JSX.Element {
  return (
    <Root {...props} className={cn(className, styles.withHead)}>
      <Head>
        {title !== undefined && <div className={styles.title}>{title}</div>}
      </Head>
      {children}
    </Root>
  );
}

export const Section = Object.assign(Root, { Head, Basic, WithHead });
